import { ref, onMounted, onUnmounted } from 'vue'
import { useAppUpdateStore } from '@/stores/appUpdate'

const CHECK_INTERVAL = 15 * 60 * 1000 // 15 minutes
const CURRENT_VERSION_KEY = 'app_version_build_time'

export function useVersionCheck() {
  const checkingVersion = ref(false)
  const appUpdateStore = useAppUpdateStore()

  let intervalId = null
  let currentVersion = null

  /**
   * Fetch the version.json file from the server
   */
  async function fetchServerVersion() {
    try {
      // Add timestamp to prevent caching
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      })

      if (!response.ok) {
        console.warn('Failed to fetch version.json:', response.status)
        return null
      }

      const data = await response.json()
      return data.buildTime
    } catch (error) {
      console.warn('Error fetching version:', error)
      return null
    }
  }

  /**
   * Check if a new version is available
   */
  async function checkForNewVersion() {
    if (checkingVersion.value) return
    // Prevent checking multiple times if dialog is already shown
    if (appUpdateStore.showUpdateDialog) return

    checkingVersion.value = true

    try {
      const serverVersion = await fetchServerVersion()

      if (!serverVersion) {
        checkingVersion.value = false
        return
      }

      // First time: store the current version
      if (currentVersion === null) {
        currentVersion = serverVersion
        localStorage.setItem(CURRENT_VERSION_KEY, String(serverVersion))
        checkingVersion.value = false
        return
      }

      // Check if server version is newer
      if (serverVersion > currentVersion) {
        console.log('New version detected:', {
          current: currentVersion,
          server: serverVersion
        })
        // Update currentVersion and localStorage immediately to prevent re-triggering
        // even if the page refreshes without user clicking reload
        currentVersion = serverVersion
        localStorage.setItem(CURRENT_VERSION_KEY, String(serverVersion))
        // Trigger the app update dialog
        appUpdateStore.triggerUpdate()
      }
    } catch (error) {
      console.error('Version check error:', error)
    } finally {
      checkingVersion.value = false
    }
  }

  /**
   * Handle visibility change (tab focus)
   */
  function handleVisibilityChange() {
    if (!document.hidden) {
      checkForNewVersion()
    }
  }

  /**
   * Start version checking
   */
  function startVersionCheck() {
    // Load current version from localStorage or fetch it
    /* const stored = localStorage.getItem(CURRENT_VERSION_KEY)
    if (stored) {
      currentVersion = parseInt(stored, 10)
    } */

    // Initial check
    checkForNewVersion()

    // Set up periodic checks
    intervalId = setInterval(checkForNewVersion, CHECK_INTERVAL)

    // Check on page focus
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  /**
   * Stop version checking
   */
  function stopVersionCheck() {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }

  // Lifecycle
  onMounted(() => {
    startVersionCheck()
  })

  onUnmounted(() => {
    stopVersionCheck()
  })

  return {
    checkingVersion,
    checkForNewVersion
  }
}
